<template>
  <label
    v-if="title !== 'none'"
    class="block mb-2 period-selector-label"
    :class="customClassTitle"
  >
    {{ t(title) }}
  </label>
  <div class="period-selector-group flex align-items-center">
    <InputNumber
      v-model="value"
      show-buttons
      button-layout="horizontal"
      decrement-button-icon="far fa-minus"
      increment-button-icon="far fa-plus"
      :min="minValue ?? 1"
      :disabled="disabled"
      class="period-input"
      :input-style="{ width: inputWidth || '3rem' }"
    />
    <Dropdown
      v-model="unit"
      :options="units"
      option-label="label"
      option-value="value"
      class="period-dropdown"
      :disabled="disabled"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted, PropType, ref, SetupContext, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputNumber from 'primevue/inputnumber';
import { DropdownOption } from '@/types';
import type { Period } from '@/types/flow-types';
import Dropdown from 'primevue/dropdown';

export default defineComponent({
  name: 'PeriodSelector',

  components: {
    InputNumber,
    Dropdown,
  },

  props: {
    modelValue: {
      type: Object as PropType<Period>,
      default: () => ({
        unit: 'MINUTE',
        value: 1,
      }),
    },

    units: {
      type: Array as PropType<DropdownOption[]>,
      required: true,
    },

    title: {
      type: String,
      required: false,
      default: 'Duration',
    },

    customClassTitle: {
      type: String,
      required: false,
      default: '',
    },

    minValue: {
      type: Number,
      required: false,
      default: 1,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    selectorGrouped: {
      type: String,
      required: false,
      default: 'period-selector-grouped',
    },

    inputWidth: {
      type: String,
      required: false,
      default: ''
    }
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: Period; units: DropdownOption[] }, { emit }: SetupContext) {
    const { t } = useI18n();
    const value = ref(props.modelValue ? props.modelValue.value : 1);
    const unit = ref(
      props.modelValue && props.units.find(u => u.value === props.modelValue.unit)
        ? props.modelValue.unit
        : props.units[0].value
    );

    const update = () => emit('update:modelValue', {
      unit: unit.value,
      value: value.value,
    });

    watch([value, unit], update);

    watch(props, () => {
      unit.value = props.modelValue.unit;
      value.value = props.modelValue.value;
    });

    // emit event if user don't change default values
    onMounted(update);

    return {
      t,
      value,
      unit,
    };
  },
});
</script>

<style lang="scss" scoped>
.period-selector-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151; // Match ButtonGroup label color
}

.period-selector-group {
  .period-input {
    :deep(.p-inputnumber-button-up) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: 0; // Merge with dropdown
      background-color: #f7f7f7;
      color: #666;
      border-color: #BABFC3;
      width: 2.5rem;
      
      &:hover {
        background-color: #f1f1f1;
        color: #333;
      }
    }

    :deep(.p-inputnumber-button-down) {
      background-color: #f7f7f7;
      color: #666;
      border-color: #BABFC3;
      width: 2.5rem;

      &:hover {
        background-color: #f1f1f1;
        color: #333;
      }
    }

    :deep(.p-inputnumber-input) {
      border-radius: 0;
      border-left: 0;
      border-right: 0;
      border-color: #BABFC3;
      text-align: center;
      width: 3rem !important;

      &:focus {
        box-shadow: none;
        border-color: #BABFC3;
      }
    }
  }

  .period-dropdown {
    :deep(.p-dropdown) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left: 1px solid #BABFC3; // Restore border separator
      height: 2.5rem;
      border-color: #BABFC3;

      &:hover {
        border-color: #BABFC3;
      }
      
      &.p-focus {
        box-shadow: none;
        border-color: #BABFC3;
      }
    }
    
    // Override PrimeVue default widths if needed, or let flex handle it
    height: 2.5rem;
    border-color: #BABFC3; // For the wrapper div of dropdown
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}
</style>
